import { css, Global } from '@emotion/react'
import { Fragment, useMemo } from 'react'
import tw, { GlobalStyles as BaseStyles } from 'twin.macro'
import { type SourceType, getSourceType } from 'utils/getSourceType'

const customStyles = (sourceType: SourceType) =>
  css({
    ':root, ::after, ::before': {
      '--primary-color': '#72bf44',
      '--primary-color-middle': '#4f9d3a',
      '--primary-color-dark': '#2d7d2f',
      '--red-light': '#fb5555',
      '--red-dark': '#c24141',
      '--yellow-light': '#fbd655',
      '--yellow-dark': '#c3801b',

      '--alpha-primary-10': 'rgba(114, 191, 68, 0.1)',
      '--alpha-black-20': 'rgba(0, 0, 0, 0.2)',
      '--alpha-white-6': 'rgba(255, 255, 255, 0.06)',
      '--alpha-white-25': 'rgba(255, 255, 255, 0.25)',

      scrollbarColor: '#4f9d3a',
      scrollbarWidth: 'thin'
    },
    '.light': {
      '--logo': '#231f20',
      '--white': '#ffffff',
      '--black': '#17191a',
      '--alpha': 'var(--grey-middle)',

      '--green-light': '#e7f0e1',
      '--grey-light': '#f5f6f7',
      '--grey-middle': '#e8eaeb',
      '--grey-dark': '#7b7b7d',
      '--grey-stroke': '#f5f6f7',

      '--text-secondary': 'var(--dark-grey)',
      '--text-main': 'var(--black)',
      '--text-error': 'var(--red-dark)',
      '--text-primary-hover': 'var(--primary-color-dark)',

      '--tertiary': 'var(--grey-light)',
      '--bg-main': 'var(--grey-light)',
      '--bg-secondary': 'var(--white)',
      '--bg-toggle-button': 'var(--alpha-primary-10)',
      '--bg-tooltip': '#F5F6F8',

      '--bg-input': 'var(--grey-light)',
      '--input-active': 'var(--primary-color-middle)',
      '--input-error': 'var(--red-dark)'
    },
    '.dark': {
      '--logo': 'var(--white)',
      '--white': 'rgba(255, 255, 255, 0.87)',
      '--black': '#1a1a1a',
      '--green-light': '#eafae6',
      '--grey-light': '#b7bbbd',
      '--grey-middle': '#282828',
      '--grey-dark': '#7b7b7d',
      '--grey-stroke': 'var(--black)',
      '--alpha': 'var(--alpha-white-6)',

      '--text-secondary': '#b7bbbd',
      '--text-main': 'var(--white)',
      '--text-error': 'var(--red-dark)',
      '--text-primary-hover': 'var(--primary-color-middle)',

      '--tertiary': '#454545',
      '--bg-main': 'var(--black)',
      '--bg-secondary': 'var(--grey-middle)',
      '--bg-toggle-button': 'var(--alpha-primary-10)',
      '--bg-input': 'var(--alpha-black-20)',
      '--bg-tooltip': '#17191A',

      '--input-active': 'var(--primary-color)',
      '--input-error': 'var(--red-light)'
    },

    body: tw`bg-main text-main antialiased font-sans overflow-x-hidden`,

    'input, textarea, button, select, a': {
      WebkitTapHighlightColor: 'rgba(0, 0, 0, 0)'
    },

    '::-webkit-scrollbar': tw`w-2 h-2`,

    '::-webkit-scrollbar-track': {
      background: sourceType === 'web' ? 'var(--bg-primary)' : undefined
    },

    '::-webkit-scrollbar-thumb': sourceType === 'web' && tw`bg-[#4f9d3a] border-[3px] border-[#4f9d3a] rounded-2xl `
  })

const GlobalStyles = () => {
  const sourceType = getSourceType()

  const styles = useMemo(() => customStyles(sourceType), [sourceType])

  return (
    <Fragment>
      <BaseStyles />
      <Global styles={styles} />
    </Fragment>
  )
}

export default GlobalStyles
